/**
 * Nutrient target resolution — web copy.
 * LINE-EQUIVALENT with services/kidney/targetResolution.ts (mobile, the
 * contract file) and functions/src/utils/targetResolution.ts. A jest test in
 * the mobile repo asserts the bodies below the marker stay identical.
 * Edit all three together.
 */

import type {
  KidneyProfile,
  OptionalNutrientKey,
  TrackedNutrientKey,
} from '@/types'

// IMPORTS-END (everything below this line is asserted identical across copies)

export interface ResolvedNutrientTarget {
  min?: number;
  max?: number;
  /**
   * custom          — from nutrientTargets or a legacy doctor-set limit
   * legacy-min-mode — 4.31-style targetModes[key]==='min' + legacy value
   * calculated      — core fallback ceiling supplied by the caller
   * none            — nothing set (informational / track-without-target)
   */
  source: 'custom' | 'legacy-min-mode' | 'calculated' | 'none';
}

export type ResolvedNutrientTargets = Record<TrackedNutrientKey, ResolvedNutrientTarget>;

/** Core fallback ceilings, computed by the caller with its platform helpers. */
export interface CalculatedFallbackMaxes {
  sodium: number;
  potassium: number;
  phosphorus: number;
  protein: number;
  fluid: number;
}

export const CORE_TARGET_KEYS = ['sodium', 'potassium', 'phosphorus', 'protein', 'fluid'] as const;

export const OPTIONAL_TARGET_KEYS: readonly OptionalNutrientKey[] = [
  'calories', 'carbs', 'fat', 'magnesium', 'calcium', 'vitaminK', 'iron', 'oxalate',
  'sugars', 'fiber', 'saturatedFat',
] as const;

/** Nutrients where a floor is clinically meaningless — a min is never resolved. */
export const NO_MIN_KEYS: readonly OptionalNutrientKey[] = ['sugars', 'saturatedFat', 'oxalate'] as const;

type CoreKey = (typeof CORE_TARGET_KEYS)[number];

const LEGACY_CORE_FIELD: Record<CoreKey, 'dailySodiumLimit' | 'dailyPotassiumLimit' | 'dailyPhosphorusLimit' | 'dailyProteinLimit' | 'dailyFluidLimit'> = {
  sodium: 'dailySodiumLimit',
  potassium: 'dailyPotassiumLimit',
  phosphorus: 'dailyPhosphorusLimit',
  protein: 'dailyProteinLimit',
  fluid: 'dailyFluidLimit',
};

const LEGACY_OPTIONAL_FIELD: Record<OptionalNutrientKey, 'dailyCaloriesLimit' | 'dailyCarbsLimit' | 'dailyFatLimit' | 'dailyMagnesiumLimit' | 'dailyCalciumLimit' | 'dailyVitaminKLimit' | 'dailyIronLimit' | 'dailyOxalateLimit' | 'dailySugarsLimit' | 'dailyFiberLimit' | 'dailySaturatedFatLimit'> = {
  calories: 'dailyCaloriesLimit',
  carbs: 'dailyCarbsLimit',
  fat: 'dailyFatLimit',
  magnesium: 'dailyMagnesiumLimit',
  calcium: 'dailyCalciumLimit',
  vitaminK: 'dailyVitaminKLimit',
  // 4.39: iron (mg) has no default anywhere — only a user-entered goal resolves.
  iron: 'dailyIronLimit',
  oxalate: 'dailyOxalateLimit',
  sugars: 'dailySugarsLimit',
  fiber: 'dailyFiberLimit',
  saturatedFat: 'dailySaturatedFatLimit',
};

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

/** A canonical entry counts only if at least one bound is a usable number. */
function fromCanonical(entry: { min?: number; max?: number } | undefined, allowMin: boolean): ResolvedNutrientTarget | null {
  if (!entry) return null;
  const min = allowMin && isFiniteNumber(entry.min) ? entry.min : undefined;
  const max = isFiniteNumber(entry.max) ? entry.max : undefined;
  if (min === undefined && max === undefined) return null;
  return { min, max, source: 'custom' };
}

/**
 * Resolve every tracked nutrient's daily target.
 *
 * `profile` may be null/undefined (returns all-'none' plus calculated core
 * ceilings). Malformed values (non-numeric, zero, negative, min on a NO_MIN
 * nutrient) are ignored rather than propagated.
 */
export function resolveNutrientTargets(
  profile: Pick<KidneyProfile, 'nutrientTargets' | 'dailySodiumLimit' | 'dailyPotassiumLimit' | 'dailyPhosphorusLimit' | 'dailyProteinLimit' | 'dailyFluidLimit' | 'optionalNutrients'> | null | undefined,
  calculated: CalculatedFallbackMaxes,
): ResolvedNutrientTargets {
  const targets = profile?.nutrientTargets;
  const optional = profile?.optionalNutrients;
  const modes = optional?.targetModes;

  const result = {} as ResolvedNutrientTargets;

  for (const key of CORE_TARGET_KEYS) {
    const canonical = fromCanonical(targets?.[key], true);
    if (canonical) {
      result[key] = canonical;
      continue;
    }
    const legacy = profile?.[LEGACY_CORE_FIELD[key]];
    if (isFiniteNumber(legacy)) {
      result[key] = { max: legacy, source: 'custom' };
      continue;
    }
    const fallback = calculated[key];
    result[key] = isFiniteNumber(fallback)
      ? { max: fallback, source: 'calculated' }
      : { source: 'none' };
  }

  for (const key of OPTIONAL_TARGET_KEYS) {
    const allowMin = !NO_MIN_KEYS.includes(key);
    const canonical = fromCanonical(targets?.[key], allowMin);
    if (canonical) {
      result[key] = canonical;
      continue;
    }
    const legacy = optional?.[LEGACY_OPTIONAL_FIELD[key]];
    if (!isFiniteNumber(legacy)) {
      result[key] = { source: 'none' };
      continue;
    }
    if (allowMin && modes?.[key] === 'min') {
      result[key] = { min: legacy, source: 'legacy-min-mode' };
    } else {
      result[key] = { max: legacy, source: 'custom' };
    }
  }

  return result;
}

/** True when the nutrient's only bound is a floor (no ceiling to score against). */
export function isMinOnly(target: ResolvedNutrientTarget | undefined): boolean {
  return !!target && target.min !== undefined && target.max === undefined;
}
